import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';

/**
 * Returns the list of pubkeys the current user follows (NIP-02 kind 3).
 *
 * Failure handling matters here: the home feed is gated on this list, so a
 * transient timeout must not be mistaken for "follows nobody". Query errors
 * (including timeouts) propagate so TanStack Query retries them; only a
 * successful query that returns no event produces an empty list.
 */
export function useFollowing() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery<string[]>({
    queryKey: ['nostr', 'following', user?.pubkey ?? ''],
    queryFn: async ({ signal }) => {
      if (!user?.pubkey) return [];
      const [event] = await nostr.query(
        [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
        // Generous timeout: on a cold start the relay sockets may still be
        // connecting. A timeout aborts the query, which TanStack treats as a
        // retryable error rather than an empty result.
        { signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]) },
      );
      if (!event) return [];
      // p tags: ["p", pubkey, relay?, petname?]
      return event.tags.filter(([t]) => t === 'p').map(([, pk]) => pk).filter(Boolean);
    },
    enabled: !!user?.pubkey,
    staleTime: 2 * 60 * 1000,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
  });
}
